
import { Link } from 'react-router-dom';
import { Phone, Mail, ChevronRight, Building } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface ContactSpecialistsProps {
  city: string;
}

const ContactSpecialists = ({ city }: ContactSpecialistsProps) => {
  return (
    <Card className="border-0 overflow-hidden shadow-lg">
      {/* Header with theme-consistent gradient background */}
      <div className="bg-gradient-to-r from-propertylk-darkblue to-propertylk-blue p-6 text-white">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
            <Building className="h-6 w-6" />
          </div>
          <h2 className="text-2xl font-bold">Expert Property Assistance</h2>
        </div>
        <p className="text-center text-white/90 max-w-2xl mx-auto">
          Connect with our commercial real estate specialists in Sri Lanka for personalized advice on property investments
        </p>
      </div>
      
      {/* Cards Container */}
      <CardContent className="p-6 bg-gradient-to-b from-propertylk-lightblue/30 to-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Investment Opportunities Card */}
          <div className="bg-white rounded-xl p-5 shadow-md border border-propertylk-blue/10 hover:shadow-lg transition-all hover:border-propertylk-blue/30 group flex flex-col h-full">
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-propertylk-darkblue">Investment Opportunities</h3>
                <div className="w-20 h-1 bg-propertylk-blue mt-2 mb-3 rounded-full"></div>
              </div>
              <div className="bg-propertylk-lightblue p-2 rounded-full text-propertylk-blue">
                <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            
            <p className="text-gray-600 flex-grow">
              Learn about potential ROI and investment strategies for commercial properties in {city}. Our specialists can guide you through the market trends.
            </p>
            
            <div className="mt-5">
              <Button 
                className="w-full bg-gradient-to-r from-propertylk-blue to-propertylk-darkblue hover:from-propertylk-darkblue hover:to-propertylk-darkblue text-white transition-all group"
                asChild
              >
                <Link to="/contact" className="flex items-center justify-center">
                  <Phone className="mr-2 h-4 w-4" />
                  <span>Speak with an Investment Advisor</span>
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Financing Options Card */}
          <div className="bg-white rounded-xl p-5 shadow-md border border-propertylk-blue/10 hover:shadow-lg transition-all hover:border-propertylk-blue/30 group flex flex-col h-full">
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-propertylk-darkblue">Financing Solutions</h3>
                <div className="w-20 h-1 bg-propertylk-blue mt-2 mb-3 rounded-full"></div>
              </div>
              <div className="bg-propertylk-lightblue p-2 rounded-full text-propertylk-blue">
                <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            
            <p className="text-gray-600 flex-grow">
              Explore financing options and lending solutions available in Sri Lanka. Get expert advice on securing the best terms for your property investment.
            </p>
            
            <div className="mt-5">
              <Button 
                variant="outline"
                className="w-full border-propertylk-blue text-propertylk-blue hover:bg-propertylk-lightblue transition-all group"
                asChild
              >
                <Link to="/contact" className="flex items-center justify-center">
                  <Mail className="mr-2 h-4 w-4" />
                  <span>Request Financing Information</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Bottom CTA */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm">
            Have questions about commercial properties in Sri Lanka? Our team is ready to assist you.
          </p>
          <div className="flex justify-center mt-2">
            <Link to="/contact" className="text-propertylk-blue hover:text-propertylk-darkblue text-sm font-medium flex items-center">
              Contact our team
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContactSpecialists;
